// Route gates and the two outer layouts.
//   RootLayout   — hash routes from the auth API → paths, document language/theme, the 401 handler, imperative dialogs
//                  (inside the router: their content may link)
//   RequireAuth  — GET /api/v1/auth/me: loading → skeleton, not signed in → /login?next=<address>, first-sign-in gates open
//                  → /login?gate=1 (the gate steps); otherwise provides the person and effective permissions to everything below
//   BareLayout   — screens without the shell (sign-in): body.is-bare hides rail/header/footer like the mockup layout:'bare'
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useLayoutEffect } from 'react';
import { Navigate, Outlet, useLocation, useNavigate } from 'react-router';
import { authFlags, authKeys, pendingGates, useMe } from '../api/auth';
import { isApiError, setUnauthorizedHandler } from '../api/client';
import { appPath } from '../api/shell';
import { AuthProvider } from '../auth/AuthContext';
import { useT } from '../i18n';
import { ModalHost } from '../ui/Modal';
import { SnackbarHost } from '../ui/Snackbar';
import { ErrorState, Skeleton } from '../ui/States';
import { AccountDisplaySync, DocumentSync } from './sync';
import { useRouteHandle } from './view';

/** A 401 from any API call: forget cached data and let RequireAuth send the person to /login (address kept). */
function UnauthorizedBridge() {
  const qc = useQueryClient();
  useEffect(
    () =>
      setUnauthorizedHandler(() => {
        authFlags.expired = true;
        qc.removeQueries({ predicate: (q) => q.queryKey[0] !== 'auth' });
        qc.setQueryData(authKeys.me, null);
      }),
    [qc],
  );
  return null;
}

/**
 * The auth API sends people back to hash routes (Google `returnTo` = '#/logs/1330?tab=qa', failure '#/login?gerr=…'),
 * the app routes paths: '/#/logs/1330?tab=qa' → '/logs/1330?tab=qa' before any gate decides.
 */
function HashRouteBridge() {
  const location = useLocation();
  const navigate = useNavigate();
  useLayoutEffect(() => {
    const to = appPath(location.hash);
    if (to) navigate(to, { replace: true });
  }, [location.hash, navigate]);
  return null;
}

export function RootLayout() {
  return (
    <>
      <HashRouteBridge />
      <DocumentSync />
      <UnauthorizedBridge />
      <Outlet />
      <ModalHost />
      <SnackbarHost />
    </>
  );
}

const loginPath = (next: string, extra = '') => `/login?next=${encodeURIComponent(next)}${extra}`;

export function RequireAuth() {
  const t = useT();
  const me = useMe();
  const location = useLocation();
  if (me.isPending)
    return (
      <main id="main" className="app-main" aria-busy="true" aria-label={t('app.loading')}>
        <Skeleton lines={6} />
      </main>
    );
  if (me.isError)
    return (
      <main id="main" className="app-main">
        <ErrorState title={t('state.failTitle')} desc={t('card.loadErrorDesc')} code={isApiError(me.error) ? (me.error.requestId ?? me.error.code) : null} onRetry={() => void me.refetch()} />
      </main>
    );
  const next = location.pathname + location.search;
  if (!me.data) return <Navigate to={loginPath(next)} replace />;
  // first-sign-in gates (password · pledge · MFA · consent) are passed on the sign-in screen before the shell opens
  if (pendingGates(me.data).length) return <Navigate to={loginPath(next, '&gate=1')} replace />;
  return (
    <AuthProvider me={me.data}>
      <AccountDisplaySync me={me.data} />
      <Outlet />
    </AuthProvider>
  );
}

export function BareLayout() {
  const t = useT();
  const handle = useRouteHandle();
  useLayoutEffect(() => {
    document.body.classList.add('is-bare');
    return () => document.body.classList.remove('is-bare');
  }, []);
  const page = handle ? t(handle.titleKey) : '';
  useEffect(() => {
    if (page) document.title = t('app.docTitle', { page });
  }, [page, t]);
  return (
    <>
      <a
        className="skip-link"
        href="#main"
        onClick={(e) => {
          e.preventDefault();
          document.getElementById('main')?.focus();
        }}
      >
        {t('app.skip')}
      </a>
      <main id="main" className="app-main" tabIndex={-1}>
        <Outlet />
      </main>
    </>
  );
}
